<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import LabelText from '~/components/form-elements/LabelText.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import AddressSelect from '~/components/form-elements/select/AddressSelect.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import OrderShippingType from '~/enums/order-shipping-type'
import helpers from '~/helpers'
import { useCartStore } from '~/store/cart'
import { useDadataStore } from '~/store/dadata'
import { useDalliStore } from '~/store/dalli'
import type { AddressSuggestionData } from '~/types/dadata'
import type { SelectedItem, RadioButtonChoice } from '~/types/form-elements'
import type { DalliCourierData } from '~/types/shipping'

const model = defineModel<boolean>()

const messages = {
  failedToCalculateShippingCost: 'Не удалось рассчитать стоимость доставки.'
}

const { $apiHelper, $toast } = useNuxtApp()
const cartStore = useCartStore()
const dadataStore = useDadataStore()
const { getAddressSuggestions } = storeToRefs(dadataStore)
const dalliStore = useDalliStore()
const { getShippingDates, getShippingIntervals } = storeToRefs(dalliStore)

const addressSuggestionData = ref<AddressSuggestionData | null>(null)
const deliveryDate = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const isLoading = ref(false)
const serviceCode = ref<number | null>(null)
const shippingCostInKopecks = ref<any>(null)
const shippingInterval = ref<any>(null)
const shippingZone = ref<number>(0)

const shippingIntervalsChoices = computed(() =>
  getShippingIntervals.value.map(
    (x: any): RadioButtonChoice => ({ labelText: `${x.minTime}:00 - ${x.maxTime}:00`, value: x })
  )
)

function beforeOpen (): void {
}

function close (): void {
  model.value = false
}

function closed (): void {
  dispose()
}

function confirmSelection (): void {
  cartStore.shippingCostInKopecks = shippingCostInKopecks.value
  cartStore.shippingData = {
    dalliAddress: addressSuggestionData.value?.meta.unrestrictedValue,
    dalliDate: deliveryDate.value,
    dalliMaxTime: shippingInterval.value.maxTime,
    dalliMinTime: shippingInterval.value.minTime,
    dalliServiceCode: serviceCode.value?.toString(),
    dalliZone: shippingZone.value?.toString()
  } as DalliCourierData

  close()
}

function dispose (): void {
  deliveryDate.value = null
  errorMessage.value = null
  serviceCode.value = null
  shippingCostInKopecks.value = null
  shippingInterval.value = null
  shippingZone.value = 0
}

function setAddress (x: SelectedItem): void {
  if (!x) {
    dispose()
    dalliStore.clearShippingDates()
    dalliStore.clearShippingIntervals()
    return;
  }

  const addressData = getAddressSuggestions.value?.find((address: AddressSuggestionData) => address.id === x.id)
  const addressAttributes = addressData?.attributes

  if (!addressAttributes) {
    return
  }

  addressSuggestionData.value = addressData

  if (addressAttributes.region === 'Москва'
  || (addressAttributes.region === 'Московская' && addressAttributes.regionType === 'обл')
  ) {
    serviceCode.value = 1
  } else if (
    addressAttributes.region === 'Санкт-Петербург'
    || (addressAttributes.region === 'Ленинградская' && addressAttributes.regionType === 'обл')
  ) {
    serviceCode.value = 11
  } else {
    serviceCode.value = 22
  }

  invokeGetShippingIntervals()
}

function invokeGetShippingIntervals (): void {
  if (deliveryDate.value === null || serviceCode.value === null) {
    return
  }

  dalliStore.fetchShippingIntervals(deliveryDate.value, serviceCode.value, shippingZone.value)
}

async function calculate (): Promise<void> {
  isLoading.value = true

  errorMessage.value = null
  shippingCostInKopecks.value = null
  shippingZone.value = 0

  try {
    const response = await $apiHelper.actions.getShippingCost(
      OrderShippingType.DalliCourier,
      cartStore.weightInGrams!,
      {
        address: addressSuggestionData.value?.meta.unrestrictedValue,
        declaredValueInKopecks: cartStore.subtotalWithDiscountInKopecks
      }
    ) as any
    errorMessage.value = response.meta.errorMessage

    shippingCostInKopecks.value = response.meta.shippingCostInKopecks
    shippingZone.value = response.meta.meta.zone ?? 0
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToCalculateShippingCost)
  }

  isLoading.value = false
}

watch(() => addressSuggestionData.value, () => {
  const fiasId = addressSuggestionData.value?.attributes.fiasId

  if (fiasId) {
    dalliStore.fetchAvailableShippingDates(fiasId)
  }
})

watch (() => deliveryDate.value, () => {
  calculate()
})

onUnmounted(() => {
  dalliStore.dispose()
})
</script>

<template>
  <CustomModalWindow v-model="model" size="large" @before-open="beforeOpen" @closed="closed">
    <template #title>
      Выбор адреса доставки&nbsp;— Dalli
    </template>

    <Notification v-if="errorMessage" class="mb-6" is-small status="error">
      {{ errorMessage }}
    </Notification>

    <Notification v-if="shippingCostInKopecks !== null" class="mb-2" is-small status="info">
      Стоимость доставки: {{ helpers.formatPrice(shippingCostInKopecks, true) }}&nbsp;₽.
    </Notification>

    <Notification class="mb-3" is-small :status="!addressSuggestionData ? 'info' : 'error'">
      Пожалуйста, указывайте адрес доставки с&nbsp;точностью до&nbsp;квартиры.
    </Notification>

    <AddressSelect class="mb-6" @set-address="setAddress" />

    <LabelText class="mb-2" is-required value="Выберите дату доставки" />

    <Notification v-if="!getShippingDates.length" is-small status="warning">
      Укажите адрес.
    </Notification>

    <div class="mb-6 flex flex-wrap">
      <BasicButton
        v-for="(shippingDate, index) in getShippingDates"
        :key="index"
        class="mb-2 mr-2 w-min"
        :color="deliveryDate === shippingDate ? 'green' : 'body-background'"
        :title="helpers.formatDate(shippingDate)"
        @click="deliveryDate = shippingDate; invokeGetShippingIntervals()"
      />
    </div>

    <RadioButtons
      v-model="shippingInterval"
      :choices="shippingIntervalsChoices"
      is-vertical
      label-text="Выберите время"
      name="shippingInterval"
    />

    <Notification v-if="!shippingIntervalsChoices.length && serviceCode" is-small status="warning">
      В указанный день доставка не осуществляется.
    </Notification>

    <Notification v-else-if="!shippingIntervalsChoices.length" is-small status="warning">
      Укажите адрес и выберите дату доставки.
    </Notification>

    <BasicButton
      v-if="errorMessage === null && shippingCostInKopecks !== null"
      class="mt-6"
      color="green"
      :is-loading="isLoading"
      title="Подтвердить выбор"
      @click="confirmSelection"
    />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
